import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { ObjectIdString } from '../types/common';

interface FetchResourceConfig<TParams, TResponse> {
    fetchFn: (token: string, params: TParams) => Promise<TResponse>;
    cacheMap: Map<string, TResponse>;
    getCacheKey: (params: TParams) => ObjectIdString;
}

// Module-level in-flight registry keyed by the caller's cacheMap. Since each consumer of
// useResource / usePublicResource owns a module-scoped cacheMap (see useProjects, useSites,
// useSimpleForm, etc.), keying pending requests by that cacheMap makes deduplication global
// per resource kind — two components mounting concurrently share the same in-flight promise
// instead of each firing its own HTTP request.
// WeakMap so a discarded cacheMap frees its pending map for GC.
const pendingRequestsByCacheMap = new WeakMap<
    Map<string, unknown>,
    Map<string, Promise<unknown>>
>();

function getPendingMap<TResponse>(
    cacheMap: Map<string, TResponse>,
): Map<string, Promise<TResponse>> {
    const asUnknown = cacheMap as unknown as Map<string, unknown>;
    let pending = pendingRequestsByCacheMap.get(asUnknown);
    if (!pending) {
        pending = new Map();
        pendingRequestsByCacheMap.set(asUnknown, pending);
    }
    return pending as Map<string, Promise<TResponse>>;
}

export const useResource = <TParams, TResponse>({
    fetchFn,
    cacheMap,
    getCacheKey,
}: FetchResourceConfig<TParams, TResponse>) => {
    const { getAccessTokenSilently } = useAuth0();

    const fetchResource = useCallback(
        async (params: TParams): Promise<TResponse> => {
            try {
                const cacheKey = getCacheKey(params);

                const cachedResult = cacheMap.get(cacheKey);
                if (cachedResult) {
                    return cachedResult;
                }

                const pending = getPendingMap<TResponse>(cacheMap);
                const pendingRequest = pending.get(cacheKey);
                if (pendingRequest) {
                    return await pendingRequest;
                }

                const requestPromise = (async () => {
                    try {
                        const token = await getAccessTokenSilently();
                        const data = await fetchFn(token, params);
                        cacheMap.set(cacheKey, data);
                        return data;
                    } finally {
                        pending.delete(cacheKey);
                    }
                })();

                pending.set(cacheKey, requestPromise);

                return await requestPromise;
            } catch (error) {
                if (error instanceof Error) {
                    throw error;
                }
                throw new Error(
                    error instanceof Object
                        ? JSON.stringify(error)
                        : String(error),
                );
            }
        },
        [getAccessTokenSilently, fetchFn, cacheMap, getCacheKey],
    );

    return { fetchResource };
};
export const usePublicResource = <TParams, TResponse>({
    fetchFn,
    cacheMap,
    getCacheKey,
}: FetchResourceConfig<TParams, TResponse>) => {
    const fetchResource = useCallback(
        async (params: TParams): Promise<TResponse> => {
            try {
                const cacheKey = getCacheKey(params);

                const cachedResult = cacheMap.get(cacheKey);
                if (cachedResult) {
                    return cachedResult;
                }

                const pending = getPendingMap<TResponse>(cacheMap);
                const pendingRequest = pending.get(cacheKey);
                if (pendingRequest) {
                    return await pendingRequest;
                }

                const requestPromise = (async () => {
                    try {
                        const data = await fetchFn('', params);
                        cacheMap.set(cacheKey, data);
                        return data;
                    } finally {
                        pending.delete(cacheKey);
                    }
                })();

                pending.set(cacheKey, requestPromise);

                return await requestPromise;
            } catch (error) {
                if (error instanceof Error) {
                    throw error;
                }
                throw new Error(
                    error instanceof Object
                        ? JSON.stringify(error)
                        : String(error),
                );
            }
        },
        [fetchFn, cacheMap, getCacheKey],
    );

    return { fetchResource };
};
